import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Upload,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { useProspects } from '@/hooks/useProspects';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { useToast } from '@/hooks/use-toast';
import { CSVUploadResult } from '@/types/prospect';

interface CSVUploaderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
}

export function CSVUploader({ open, onOpenChange, listId }: CSVUploaderProps) {
  const { addProspectsToList } = useOptimizedCampaignData();
  const { parseCSV } = useProspects();
  const { toast } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadResult, setUploadResult] = useState<CSVUploadResult | null>(
    null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      setUploadResult(null);
    } else {
      toast({
        title: 'Invalid File',
        description: 'Please select a valid CSV file.',
        variant: 'destructive',
      });
    }
  };

  const processFile = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    try {
      const text = await selectedFile.text();
      const result = parseCSV(text);
      setUploadResult(result);
    } catch (error) {
      toast({
        title: 'Processing Error',
        description: 'Failed to process the CSV file.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmImport = () => {
    if (uploadResult && uploadResult.successful.length > 0) {
      addProspectsToList(listId, uploadResult.successful);

      // Check for invalid character errors specifically
      const hasInvalidCharacters = uploadResult.failed.some((f) =>
        f.error.includes('Invalid characters in phone')
      );

      let description = `${uploadResult.successful.length} prospects have been imported. Only entries with a complete phone entry were added.`;
      if (hasInvalidCharacters) {
        description += ` Some entries were found with invalid characters in the phone field and were not imported.`;
      }

      toast({
        title: 'Import Successful',
        description,
      });
      onOpenChange(false);
      resetState();
    }
  };

  const resetState = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csvContent =
      'Name,Phone,Company,Email\nJohn Smith,(555) 123-4567,ABC HVAC Services,john@abchvac.com\nJane Doe,(555) 987-6543,XYZ Plumbing,jane@xyzplumbing.com\nCarlos Rivera,+50769285069,Panama Services,carlos@panamaservices.com';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prospect_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Upload Prospects via CSV</DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {/* Instructions */}
          <Alert>
            <FileText className='h-4 w-4' />
            <AlertDescription>
              <div className='space-y-2'>
                <p>
                  <strong>Required columns:</strong> Name, Phone, Company
                </p>
                <p>
                  <strong>Optional columns:</strong> Email
                </p>
                <p>Make sure your CSV file has headers in the first row.</p>
                <p>
                  <strong>Phone format:</strong> US/Canada enter normally, e.g. (555) 123-4567. Other countries include + and country code, no spaces, e.g. +50769285069
                </p>
              </div>
            </AlertDescription>
          </Alert>

          {/* Template Download */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>Need a Template?</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant='outline'
                onClick={downloadTemplate}
                className='gap-2'
              >
                <Download className='h-4 w-4' />
                Download CSV Template
              </Button>
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle className='text-lg'>Upload Your CSV</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='border-2 border-dashed border-gray-300 rounded-lg p-6 text-center'>
                <Upload className='h-12 w-12 mx-auto mb-4 text-gray-400' />
                <div className='space-y-2'>
                  <p className='text-sm text-gray-600'>
                    {selectedFile
                      ? selectedFile.name
                      : 'Choose a CSV file to upload'}
                  </p>
                  <Button
                    variant='outline'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessing}
                  >
                    Select File
                  </Button>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.csv'
                    onChange={handleFileSelect}
                    className='hidden'
                  />
                </div>
              </div>

              {selectedFile && !uploadResult && (
                <Button
                  onClick={processFile}
                  disabled={isProcessing}
                  className='w-full'
                >
                  {isProcessing ? 'Processing...' : 'Preview Import'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Upload Results */}
          {uploadResult && (
            <Card>
              <CardHeader>
                <CardTitle className='text-lg'>Import Preview</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='grid grid-cols-3 gap-4'>
                  <div className='text-center p-3 bg-green-50 rounded-lg'>
                    <CheckCircle className='h-8 w-8 text-green-600 mx-auto mb-2' />
                    <div className='text-2xl font-bold text-green-600'>
                      {uploadResult.successful.length}
                    </div>
                    <div className='text-sm text-green-700'>Successful</div>
                  </div>

                  <div className='text-center p-3 bg-red-50 rounded-lg'>
                    <XCircle className='h-8 w-8 text-red-600 mx-auto mb-2' />
                    <div className='text-2xl font-bold text-red-600'>
                      {uploadResult.failed.length}
                    </div>
                    <div className='text-sm text-red-700'>Failed</div>
                  </div>

                  <div className='text-center p-3 bg-yellow-50 rounded-lg'>
                    <AlertTriangle className='h-8 w-8 text-yellow-600 mx-auto mb-2' />
                    <div className='text-2xl font-bold text-yellow-600'>
                      {uploadResult.duplicates.length}
                    </div>
                    <div className='text-sm text-yellow-700'>Duplicates</div>
                  </div>
                </div>

                {/* Error Details */}
                {uploadResult.failed.length > 0 && (
                  <Alert variant='destructive'>
                    <XCircle className='h-4 w-4' />
                    <AlertDescription>
                      <div className='space-y-1'>
                        <p>
                          <strong>Errors found:</strong>
                        </p>
                        {uploadResult.failed.slice(0, 3).map((error, index) => (
                          <p key={index} className='text-xs'>
                            Row {error.row}: {error.error}
                          </p>
                        ))}
                        {uploadResult.failed.length > 3 && (
                          <p className='text-xs'>
                            ...and {uploadResult.failed.length - 3} more errors
                          </p>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Success Confirmation */}
                {uploadResult.successful.length > 0 && (
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      onClick={() => setUploadResult(null)}
                      className='flex-1'
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirmImport}
                      className='flex-1 bg-green-600 hover:bg-green-700'
                    >
                      Import {uploadResult.successful.length} Prospects
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
